import { useQuery } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import {
  ActiveSourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import { useEffect, useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useOpeningContext } from '@/contexts/opening-context';
import { useDataTable } from '@/hooks/use-data-table';

import { useChannelsStore } from '../use-channels-store';
import { useChannelColumns } from './columns';
import { useReachChannels } from './use-reach-channels';

interface ReachChannelsProps {
  sourcingPurchaseId: string;
  gridTemplateColumns: string;
}

export function ReachChannels({
  sourcingPurchaseId,
  gridTemplateColumns,
}: ReachChannelsProps) {
  const { reachPublishers, setReachPublishers, setReachPublisherStatus } =
    useReachChannels();
  const setRefetchReachChannels = useChannelsStore(
    s => s.setRefetchReachChannels,
  );
  const { showAggregateData, currentHiringGoalId } = useOpeningContext();

  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: [
      'reachChannels',
      sourcingPurchaseId,
      showAggregateData,
      currentHiringGoalId,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingPurchasesCampaignDetails(
        sourcingPurchaseId,
        showAggregateData,
        currentHiringGoalId,
      ),
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const campaigns = useMemo(() => data?.reach_campaigns ?? [], [data]);

  useEffect(() => {
    setReachPublishers(campaigns);
  }, [campaigns, setReachPublishers]);

  const columns = useChannelColumns({
    setReachPublisherStatus,
  });

  const { table } = useDataTable<ActiveSourcingChannelDetails>({
    columns,
    data: reachPublishers,
    rowCount: reachPublishers.length,
  });

  useEffect(() => {
    setRefetchReachChannels(async () => {
      await refetch();
    });
  }, [refetch, setRefetchReachChannels]);

  if (isFetching)
    return (
      <ReachChannelsSkeleton
        gridTemplateColumns={gridTemplateColumns}
        columnsLength={columns.length}
      />
    );

  return (
    <>
      {table.getRowModel().rows.map(row => (
        <div
          key={row.id}
          className="reach-campaigns-text grid items-start"
          style={{ gridTemplateColumns }}
        >
          {row.getVisibleCells().map(cell => (
            <div
              key={cell.id}
              className={clsx(
                'flex flex-col items-start gap-2 py-6 px-5',
                cell.column.id === 'edit' && 'place-self-center',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function ReachChannelsSkeleton({
  gridTemplateColumns,
  columnsLength,
}: {
  gridTemplateColumns: string;
  columnsLength: number;
}) {
  return (
    <>
      {_range(2).map(rowIndex => (
        <div
          key={rowIndex}
          className="grid items-center"
          style={{ gridTemplateColumns }}
        >
          <div className="h-21 flex flex-col items-start gap-2 py-6 px-5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
          {_range(columnsLength - 2).map(colIndex => (
            <div
              key={colIndex}
              className="h-21 flex flex-col items-start gap-3 py-6 px-5"
            >
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-1.5 w-full" />
            </div>
          ))}
          <div className="h-21 flex flex-col items-center justify-between gap-2 py-6 px-5">
            <Skeleton className="h-4 w-8" />
          </div>
        </div>
      ))}
    </>
  );
}
